import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  LinearProgress,
  Chip,
  Divider,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  Pool as PoolIcon,
  Warning as WarningIcon,
  People as PeopleIcon,
  DeviceHub as DeviceIcon,
  PoolSharp as PoolFilledIcon,
  Videocam as CctvIcon,
  Watch as WatchIcon,
} from '@mui/icons-material';

// 통계 항목 인터페이스
interface StatItem {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  secondaryValue?: number | string;
  secondaryLabel?: string;
  progress?: number;
  status?: 'success' | 'warning' | 'error' | 'info';
  trend?: 'up' | 'down' | 'stable';
  trendValue?: number;
}

export interface StatsSummaryWidgetProps {
  stats: {
    poolCount: number;
    activePoolCount: number;
    cctvCount: number;
    onlineCctvCount: number;
    activeMembersCount: number;
    totalMembersCount: number;
    wearableCount: number;
    activeWearableCount: number;
    activeIncidents: number;
    resolvedIncidents: number;
    systemUptime: number;
    dataCollectionRate: number;
    readinessStatus?: string;
    readinessPass?: number;
    readinessTotal?: number;
    healthCheckSummary?: string;
    centralAiJobCount?: number;
    centralAiEnabled?: boolean;
    cctvStats: {
      total: number;
      active: number;
      maintenance: number;
      inactive: number;
    };
  };
  isLoading: boolean;
  title: string;
  subtitle: string;
}

const StatsSummaryWidget: React.FC<StatsSummaryWidgetProps> = ({
  stats,
  isLoading,
  title,
  subtitle,
}) => {
  const theme = useTheme();

  // 기본값 설정
  const {
    poolCount = 0,
    activePoolCount = 0,
    cctvCount = 0,
    onlineCctvCount = 0,
    activeMembersCount = 0,
    totalMembersCount = 0,
    wearableCount = 0,
    activeWearableCount = 0,
    activeIncidents = 0,
    resolvedIncidents = 0,
    systemUptime = 99.9,
    dataCollectionRate = 95,
    readinessStatus,
    readinessPass,
    readinessTotal,
    healthCheckSummary,
    centralAiJobCount,
    centralAiEnabled,
  } = stats;

  // 통계 항목 목록
  const statItems: StatItem[] = [
    {
      label: '시설',
      value: poolCount,
      icon: <PoolIcon />,
      color: theme.palette.primary.main,
      secondaryValue: activePoolCount,
      secondaryLabel: '활성화',
      progress: (activePoolCount / (poolCount || 1)) * 100,
      status: 'success'
    },
    {
      label: 'CCTV',
      value: cctvCount,
      icon: <CctvIcon />,
      color: theme.palette.info.main,
      secondaryValue: onlineCctvCount,
      secondaryLabel: '온라인',
      progress: (onlineCctvCount / (cctvCount || 1)) * 100,
      status: onlineCctvCount < cctvCount * 0.9 ? 'warning' : 'success'
    },
    {
      label: '웨어러블',
      value: wearableCount,
      icon: <WatchIcon />,
      color: theme.palette.secondary.main,
      secondaryValue: activeWearableCount,
      secondaryLabel: '활성화',
      progress: (activeWearableCount / (wearableCount || 1)) * 100,
      status: activeWearableCount < wearableCount * 0.8 ? 'warning' : 'success'
    },
    {
      label: '회원',
      value: totalMembersCount,
      icon: <PeopleIcon />,
      color: theme.palette.success.main,
      secondaryValue: activeMembersCount,
      secondaryLabel: '현재 이용 중',
      progress: (activeMembersCount / (totalMembersCount || 1)) * 100,
      status: 'info'
    },
    {
      label: '알림',
      value: activeIncidents + resolvedIncidents,
      icon: <WarningIcon />,
      color: theme.palette.warning.main,
      /** 미해결(처리 중) 건수는 대시보드에 표시하지 않음 */
      secondaryValue: undefined,
      secondaryLabel: undefined,
      progress: 100,
      status: 'info' as const,
    },
    {
      label: '시스템 상태',
      value:
        readinessStatus === 'ready'
          ? '정상'
          : readinessStatus === 'degraded'
            ? '주의'
            : readinessStatus === 'offline'
              ? '오프라인'
              : String(readinessStatus || `${systemUptime}%`),
      icon: <DeviceIcon />,
      color: theme.palette.info.main,
      secondaryValue: `${dataCollectionRate}%`,
      secondaryLabel: 'CCTV 수집률',
      progress: systemUptime,
      status:
        readinessStatus === 'offline' || systemUptime < 50
          ? 'error'
          : readinessStatus === 'degraded' || systemUptime < 100
            ? 'warning'
            : 'success',
    },
  ];

  // 상태에 따른 색상 설정
  const getStatusColor = (status?: 'success' | 'warning' | 'error' | 'info') => {
    switch (status) {
      case 'success':
        return theme.palette.success.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'error':
        return theme.palette.error.main;
      case 'info':
      default:
        return theme.palette.info.main;
    }
  };

  // 진행률 바 색상 설정
  const getProgressColor = (status?: 'success' | 'warning' | 'error' | 'info', progress?: number) => {
    if (progress === undefined) return theme.palette.primary.main;
    
    if (progress < 50) return theme.palette.error.main;
    if (progress < 80) return theme.palette.warning.main;
    return getStatusColor(status);
  };

  if (isLoading) {
    return (
      <Box sx={{ width: '100%', py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>통계 데이터 로드 중...</Typography>
      </Box>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" component="div" gutterBottom>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {statItems.map((stat, index) => (
            <Grid item xs={12} sm={6} md={4} lg={4} key={index}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  p: 2,
                  boxShadow: 1
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}
                  >
                    <Box sx={{ mr: 1, color: stat.color }}>{stat.icon}</Box>
                    {stat.label}
                  </Typography>
                  <Chip 
                    size="small" 
                    label={stat.status === 'success' ? '정상' : 
                           stat.status === 'warning' ? '주의' : 
                           stat.status === 'error' ? '위험' : '정보'}
                    color={stat.status}
                  />
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                    {stat.value}
                  </Typography>
                  {stat.secondaryValue !== undefined && (
                    <Tooltip
                      title={
                        stat.label === '시스템 상태'
                          ? [
                              healthCheckSummary ||
                                (readinessTotal != null
                                  ? `준비 점검 ${readinessPass}/${readinessTotal}`
                                  : ''),
                              centralAiEnabled
                                ? `중앙 AI ${centralAiJobCount ?? 0} CCTV 추론`
                                : '중앙 AI 꺼짐',
                            ]
                              .filter(Boolean)
                              .join(' · ')
                          : stat.secondaryLabel || ''
                      }
                    >
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        {stat.secondaryLabel && `${stat.secondaryLabel}: `}
                        {stat.secondaryValue}
                      </Typography>
                    </Tooltip>
                  )}
                </Box>
                
                {stat.progress !== undefined && (
                  <Box sx={{ width: '100%', mt: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={stat.progress}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: theme.palette.grey[200],
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: getProgressColor(stat.status, stat.progress),
                          borderRadius: 3,
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default StatsSummaryWidget; 